import type { GameState } from '../../engine/game-state.js';
import { fromMap } from '../../render/classic/layout.js';
import type { ClassicSlot, ClassicSpec } from '../../render/classic/types.js';
import { CARD_W } from '../../render/classic/types.js';
import { drawFromStock, hasAnyMatch, PEAK_IDS, tripeaksRules } from './rules.js';

// Pixel geometry. COL_PX ≈ 1.05 card-widths, ROW_PX = 0.5/1.4 card-heights
// (matches the Three.js spec's relative spacing 1:1).
const COL_PX = 75;
const ROW_PX = 34;
const HALF_W = CARD_W / 2;

const FELT_W = 810;
const TOP_Y = 32;
const R1_Y = TOP_Y;
const R2_Y = TOP_Y + ROW_PX;
const R3_Y = TOP_Y + ROW_PX * 2;
const R4_Y = TOP_Y + ROW_PX * 3;
const STOCK_Y = R4_Y + 96 + 36;
const FELT_H = STOCK_Y + 96 + 32;

const cx = FELT_W / 2;

// helper: convert a centered x-offset (in COL_PX units) into a left edge
const lx = (offsetCols: number) => cx + offsetCols * COL_PX - HALF_W;

const slotMap: Record<string, ClassicSlot> = {
  // Row 1 - peak tops
  p11: { x: lx(-3), y: R1_Y },
  p12: { x: lx(0),  y: R1_Y },
  p13: { x: lx(3),  y: R1_Y },
  // Row 2 - 6 cards (2 per peak, half-step out)
  p21: { x: lx(-3.5), y: R2_Y },
  p22: { x: lx(-2.5), y: R2_Y },
  p23: { x: lx(-0.5), y: R2_Y },
  p24: { x: lx( 0.5), y: R2_Y },
  p25: { x: lx( 2.5), y: R2_Y },
  p26: { x: lx( 3.5), y: R2_Y },
  // Row 3 - 9 cards (3 per peak)
  p31: { x: lx(-4), y: R3_Y },
  p32: { x: lx(-3), y: R3_Y },
  p33: { x: lx(-2), y: R3_Y },
  p34: { x: lx(-1), y: R3_Y },
  p35: { x: lx( 0), y: R3_Y },
  p36: { x: lx( 1), y: R3_Y },
  p37: { x: lx( 2), y: R3_Y },
  p38: { x: lx( 3), y: R3_Y },
  p39: { x: lx( 4), y: R3_Y },
  // Row 4 - 10 base cards
  p41:    { x: lx(-4.5), y: R4_Y },
  p42:    { x: lx(-3.5), y: R4_Y },
  p43:    { x: lx(-2.5), y: R4_Y },
  p44:    { x: lx(-1.5), y: R4_Y },
  p45:    { x: lx(-0.5), y: R4_Y },
  p46:    { x: lx( 0.5), y: R4_Y },
  p47:    { x: lx( 1.5), y: R4_Y },
  p48:    { x: lx( 2.5), y: R4_Y },
  p49:    { x: lx( 3.5), y: R4_Y },
  'p4-10':{ x: lx( 4.5), y: R4_Y },
  // Stock + waste below
  stock: { x: lx(-0.6), y: STOCK_Y },
  waste: { x: lx( 0.6), y: STOCK_Y },
};

const layout = fromMap(slotMap);

function applyDiscard(state: GameState, fromId: string, cardId: string): boolean {
  const move = { from: fromId, to: 'waste', cardId };
  if (!tripeaksRules.canMove(state, move)) return false;
  const fromPile = state.piles[fromId]!;
  const card = fromPile.cards.pop()!;
  state.piles.waste!.cards.push(card);
  state.movesCount++;
  tripeaksRules.afterMove?.(state, move);
  return true;
}

export const tripeaksClassicSpec: ClassicSpec = {
  rules: tripeaksRules,
  feltWidth: FELT_W,
  feltHeight: FELT_H,
  slotForPile: (id) => layout.slot(id),
  // No drag - every move is a click.
  dropExclude: () => [...PEAK_IDS, 'stock', 'waste'],
  dragStackIds: () => [],
  autoSendOrder: () => ['waste'],
  onPileClick(state, pile, cardId) {
    if (pile.id === 'stock') {
      if (!drawFromStock(state)) return false;
      state.movesCount++;
      return true;
    }
    if (pile.type === 'peak') {
      return applyDiscard(state, pile.id, cardId);
    }
    return false;
  },
  statusText(state) {
    const stockEmpty = state.piles.stock!.cards.length === 0;
    if (stockEmpty && !hasAnyMatch(state) && !tripeaksRules.isWon(state)) return 'No moves left';
    return null;
  },
  cascadeCardIds(state) {
    return state.piles.waste!.cards.map((c) => c.id);
  },
  showSlotMarker: () => false,
};
